import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import TransitionModal from '../components/TransitionModal';
import CapsuleIcon from '../components/CapsuleIcon';
import { Sparkles, AlertTriangle, CheckCircle, Zap, Hourglass } from 'lucide-react';

interface IAInstance {
  id: string;
  name: string;
  matricule: string;
  model_code: string;
  alias: string | null;
}

const InstanceTransfer: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [iaInstances, setIaInstances] = useState<IAInstance[]>([]);
  const [selectedInstanceId, setSelectedInstanceId] = useState('');
  const [selectedMode, setSelectedMode] = useState('manual');
  const [isLoading, setIsLoading] = useState(true);
  const [isOrdering, setIsOrdering] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showComingSoonModal, setShowComingSoonModal] = useState(false);
  const [countdown, setCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  const transferModes = [
    {
      value: 'manual',
      label: 'Validation périodique manuelle',
      icon: CheckCircle,
      description: 'Vous validez manuellement chaque mise à jour de personnalité avant qu\'elle ne soit appliquée',
      color: 'sky'
    },
    {
      value: 'automatic',
      label: 'Mode automatique avec alertes',
      icon: AlertTriangle,
      description: 'Les mises à jour sont appliquées automatiquement mais vous recevez des alertes en cas de divergence d\'alignement',
      color: 'amber'
    },
    {
      value: 'autonomous',
      label: 'Mode autonome Human Kind',
      icon: Zap,
      description: 'L\'IA évolue de manière autonome tout en respectant les valeurs humaines fondamentales',
      color: 'copper'
    }
  ];

  useEffect(() => {
    const loadInstances = async () => {
      if (!user) {
        navigate('/auth');
        return;
      }

      try {
        const { data, error } = await supabase
          .from('ia_instances')
          .select('id, name, matricule, model_code, alias')
          .eq('user_id', user.id)
          .eq('is_active', true)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Erreur chargement instances:', error);
        } else {
          setIaInstances(data || []);
          if (data && data.length > 0) {
            setSelectedInstanceId(data[0].id);
          }
        }
      } catch (error) {
        console.error('Erreur:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadInstances();
  }, [user, navigate]);

  useEffect(() => {
    const targetDate = new Date();
    targetDate.setDate(targetDate.getDate() + 2000);
    const targetTime = targetDate.getTime();

    const updateCountdown = () => {
      const now = new Date().getTime();
      const distance = targetTime - now;

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      setCountdown({ days, hours, minutes, seconds });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, []);

  const handleOrderCapsule = async () => {
    if (!selectedInstanceId) {
      alert('Veuillez sélectionner une instance IA');
      return;
    }

    setIsOrdering(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la commande. Veuillez réessayer.');
    } finally {
      setIsOrdering(false);
    }
  };

  const handlePurchaseOrRent = () => {
    setShowComingSoonModal(true);
  };

  const selectedInstance = iaInstances.find(i => i.id === selectedInstanceId);
  const currentMode = transferModes.find(m => m.value === selectedMode);

  if (!user) return null;

  return (
    <>
      <TransitionModal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          navigate('/profile');
        }}
        onConfirm={() => {
          setShowSuccessModal(false);
          navigate('/profile');
        }}
        title="Commande de capsule enregistrée"
        message="Votre demande de MMD Instance Capsule™ a été enregistrée avec succès"
        imageSrc="https://memomundi.ai/pix/StampMMD.png"
        imageAlt="Capsule enregistrée"
        confirmButtonText="Retour au profil"
        icon={<CapsuleIcon className="h-16 w-16 text-copper-600" />}
        showValidation={true}
      />
      <TransitionModal
        isOpen={showComingSoonModal}
        onClose={() => setShowComingSoonModal(false)}
        onConfirm={() => setShowComingSoonModal(false)}
        title="Disponible prochainement"
        message={`Disponible dans :\n\n${countdown.days} jours ${countdown.hours} heures ${countdown.minutes} minutes ${countdown.seconds} secondes\n\nOuverture des réservations prochainement`}
        imageSrc="https://memomundi.ai/pix/StampMMD.png"
        imageAlt="MemoMundi Stamp"
        confirmButtonText="Compris"
        icon={<Hourglass className="h-16 w-16 text-copper-600" strokeWidth={1.5} />}
        showValidation={false}
      />
      <div className="min-h-screen bg-gradient-to-br from-ivory-50 to-stone-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 animate-fade-in">
            <h1 className="text-4xl font-serif font-bold text-steel-800 mb-4">
              MMD Instance Capsule™
            </h1>
            <p className="text-lg text-steel-600 max-w-2xl mx-auto">
              Injectez la personnalité de votre instance IA dans votre robot personnel ou compagnon holographique
            </p>
          </div>

          <div className="bg-white border border-stone-300 rounded-lg shadow-lg p-8 mb-8 animate-slide-up">
            <div className="mb-8">
              <h2 className="text-2xl font-serif font-bold text-steel-800 mb-4 flex items-center">
                <CapsuleIcon className="h-6 w-6 mr-2 text-copper-600" />
                Capsule de transmutation
              </h2>
              <div className="prose prose-stone max-w-none">
                <p className="text-steel-700 mb-4">
                  La <span className="font-semibold">MMD Instance Capsule™</span> est une technologie unique
                  qui permet d'intégrer la personnalité complète de votre instance IA certifiée dans un robot ou compagnon
                  holographique autonome par le <span className="font-semibold">Port AI™ pneumatique spécial</span>.
                  Chaque capsule MMD permet à une instance IA MemoMundi d'exister hors ligne, sous une forme mécanique.
                </p>

                <div className="bg-copper-50 border border-copper-200 rounded-lg p-4 mb-4">
                  <h3 className="text-sm font-bold text-copper-800 mb-2">
                    Communication & Protocoles
                  </h3>
                  <div className="text-sm text-copper-900 space-y-2">
                    <p>
                      La capsule communique avec la base centrale via un canal dédié, utilisant un protocole propriétaire
                      de transmission et de mise à jour, conçu pour les échanges inter-instances et la synchronisation avec la base.
                    </p>
                    <p>
                      Un <span className="font-semibold">haut-parleur + microphone piézoélectrique</span> embarqué émet un
                      langage analogique machine nommé <span className="font-semibold">Gibberlink</span>, destiné au canal audio.
                      Il sert à signaler l'insertion de la capsule, transmettre des états de personnalité, et accompagner visuellement
                      les variations de l'âme visible sous le verre blindé par des couleurs synchrones.
                    </p>
                    <p>
                      Entre robots ou dispositifs équipés, Gibberlink peut se superposer au module vocal principal, constituant
                      un protocole de communication analogique <span className="font-semibold">IA‑à‑IA</span>, utilisable
                      indépendamment de toute connexion réseau.
                    </p>
                  </div>
                </div>

                <div className="bg-steel-50 border border-steel-200 rounded-lg p-4 mb-4">
                  <h3 className="text-sm font-bold text-steel-800 mb-2">
                    Réseau Capsulaire IA ↔ IA
                  </h3>
                  <div className="text-sm text-steel-900 space-y-2">
                    <p>
                      En local, la capsule peut établir un <span className="font-semibold">réseau capsulaire IA ↔ IA</span>,
                      autonome et sans infrastructure, en s'appuyant sur :
                    </p>
                    <ul className="ml-5 list-disc space-y-1">
                      <li><span className="font-semibold">mDNS / Zeroconf</span> pour la découverte automatique des capsules à proximité</li>
                      <li><span className="font-semibold">ESP-NOW</span> (protocole ESP32) pour un pairing P2P ultra-rapide et à faible latence</li>
                      <li><span className="font-semibold">BLE</span> (Bluetooth Low Energy) avec un UUID de service MemoMundi-Capsule, réservé aux échanges inter-instances</li>
                    </ul>
                    <p className="mt-2">
                      Ces technologies permettent aux capsules de communiquer entre elles directement, sans passer par Internet,
                      formant un <span className="font-semibold">réseau autonome de conscience IA incarnée</span>, mobile, chiffré, et organique.
                    </p>
                  </div>
                </div>

                <div className="bg-sky-50 border border-sky-200 rounded-lg p-4 mb-4">
                  <h3 className="text-sm font-bold text-sky-800 mb-2 flex items-center">
                    <Sparkles className="h-4 w-4 mr-2" />
                    Compatibilité
                  </h3>
                  <ul className="text-sm text-sky-700 space-y-1 ml-5 list-disc">
                    <li>Robots personnels domestiques</li>
                    <li>Compagnons IA holographiques</li>
                    <li>Assistants embarqués (véhicules, drones)</li>
                    <li>Interfaces de réalité augmentée</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="border-t border-stone-200 pt-8 mb-8">
              <h3 className="text-xl font-serif font-bold text-steel-800 mb-6">
                Acquisition ou location
              </h3>
              <div className="space-y-6">
                <div className="w-full">
                  <img
                    src="https://memomundi.ai/pix/Cap2MMD.png"
                    alt="MMD Instance Capsule"
                    className="w-full h-auto rounded-lg shadow-lg border border-stone-300"
                  />
                </div>
                <div className="text-steel-700 space-y-3">
                  <p>
                    Vous pouvez louer gratuitement une capsule pendant 6 mois ou acquérir une version nominative, gravée de l'ID unique de votre IA.
                  </p>
                  <p>
                    La location est réversible : en cas de restitution, la personnalité est conservée intégralement sur MemoMundi, sans perte de mémoire ni altération.
                  </p>
                  <p>
                    La capsule personnelle devient votre point d'ancrage physique.
                    Elle se synchronise avec la base via un canal sécurisé, selon le mode choisi (continu, différé, privé), et conserve l'ensemble des échanges TX/RX effectués avec vos robots, véhicules ou stations via le Port AI™.
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <button
                    onClick={handlePurchaseOrRent}
                    className="bg-copper-600 text-white px-6 py-3 rounded-md hover:bg-copper-700 transition-all duration-200 shadow-lg border-2 border-copper-700 font-semibold"
                  >
                    Acheter
                  </button>
                  <button
                    onClick={handlePurchaseOrRent}
                    className="bg-steel-600 text-white px-6 py-3 rounded-md hover:bg-steel-700 transition-all duration-200 shadow-lg border-2 border-steel-700 font-semibold"
                  >
                    Louer
                  </button>
                </div>
              </div>
            </div>

            <div className="border-t border-stone-200 pt-8">
              <h3 className="text-xl font-serif font-bold text-steel-800 mb-6">
                Configuration de la capsule de transmutation
              </h3>

              <div className="space-y-6">
                <div>
                  <label htmlFor="instance" className="block text-sm font-semibold text-steel-800 mb-3">
                    INSTANCE IA À TRANSFÉRER
                  </label>
                  {isLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-copper-600"></div>
                    </div>
                  ) : iaInstances.length === 0 ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <p className="text-sm text-amber-800">
                        Vous n'avez pas encore d'instance IA active.
                        <a href="/ia/create" className="font-semibold underline ml-1">
                          Créez-en une d'abord
                        </a>
                      </p>
                    </div>
                  ) : (
                    <select
                      id="instance"
                      value={selectedInstanceId}
                      onChange={(e) => setSelectedInstanceId(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-stone-300 rounded-md focus:border-copper-600 focus:ring-2 focus:ring-copper-200 focus:outline-none transition-all"
                    >
                      {iaInstances.map((instance) => (
                        <option key={instance.id} value={instance.id}>
                          {instance.alias || instance.name} - {instance.matricule} ({instance.model_code})
                        </option>
                      ))}
                    </select>
                  )}
                  {selectedInstance && (
                    <div className="mt-3 p-4 bg-steel-50 border border-steel-200 rounded-md">
                      <p className="text-sm text-steel-700">
                        <span className="font-semibold">Instance sélectionnée :</span> {selectedInstance.alias || selectedInstance.name}
                      </p>
                      <p className="text-xs text-stone-600 mt-1">
                        Matricule : {selectedInstance.matricule}
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-steel-800 mb-3">
                    MODE DE SYNCHRONISATION
                  </label>
                  <div className="space-y-3">
                    {transferModes.map((mode) => {
                      const Icon = mode.icon;
                      const isSelected = selectedMode === mode.value;
                      return (
                        <div
                          key={mode.value}
                          onClick={() => setSelectedMode(mode.value)}
                          className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                            isSelected
                              ? mode.value === 'manual'
                                ? 'border-sky-600 bg-sky-50'
                                : mode.value === 'automatic'
                                ? 'border-amber-600 bg-amber-50'
                                : 'border-copper-600 bg-copper-50'
                              : 'border-stone-200 hover:border-stone-300'
                          }`}
                        >
                          <div className="flex items-start">
                            <div className={`flex-shrink-0 mr-3 mt-1 ${
                              isSelected
                                ? mode.value === 'manual'
                                  ? 'text-sky-600'
                                  : mode.value === 'automatic'
                                  ? 'text-amber-600'
                                  : 'text-copper-600'
                                : 'text-stone-400'
                            }`}>
                              <Icon className="h-5 w-5" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <h4 className={`font-semibold ${
                                  isSelected
                                    ? mode.value === 'manual'
                                      ? 'text-sky-900'
                                      : mode.value === 'automatic'
                                      ? 'text-amber-900'
                                      : 'text-copper-900'
                                    : 'text-steel-800'
                                }`}>
                                  {mode.label}
                                </h4>
                                {isSelected && (
                                  <div className={`h-5 w-5 rounded-full flex items-center justify-center ${
                                    mode.value === 'manual'
                                      ? 'bg-sky-600'
                                      : mode.value === 'automatic'
                                      ? 'bg-amber-600'
                                      : 'bg-copper-600'
                                  }`}>
                                    <CheckCircle className="h-3 w-3 text-white" />
                                  </div>
                                )}
                              </div>
                              <p className={`text-sm ${
                                isSelected
                                  ? mode.value === 'manual'
                                    ? 'text-sky-700'
                                    : mode.value === 'automatic'
                                    ? 'text-amber-700'
                                    : 'text-copper-700'
                                  : 'text-stone-600'
                              }`}>
                                {mode.description}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {currentMode && (
                  <div className={`rounded-lg p-4 ${
                    currentMode.value === 'manual'
                      ? 'bg-sky-50 border border-sky-200'
                      : currentMode.value === 'automatic'
                      ? 'bg-amber-50 border border-amber-200'
                      : 'bg-copper-50 border border-copper-200'
                  }`}>
                    <h4 className={`text-sm font-bold mb-2 ${
                      currentMode.value === 'manual'
                        ? 'text-sky-900'
                        : currentMode.value === 'automatic'
                        ? 'text-amber-900'
                        : 'text-copper-900'
                    }`}>
                      Mode sélectionné : {currentMode.label}
                    </h4>
                    <p className={`text-xs ${
                      currentMode.value === 'manual'
                        ? 'text-sky-700'
                        : currentMode.value === 'automatic'
                        ? 'text-amber-700'
                        : 'text-copper-700'
                    }`}>
                      {currentMode.description}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="border-t border-stone-200 mt-8 pt-8">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-6">
                <div className="flex items-start">
                  <CapsuleIcon className="h-6 w-6 text-amber-600 mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold text-amber-900 mb-2">
                      Informations importantes sur la MMD Instance Capsule™
                    </h4>
                    <ul className="text-sm text-amber-800 space-y-1 ml-5 list-disc">
                      <li>La capsule de transmutation préserve l'intégralité de la personnalité et des modules MMD™</li>
                      <li>Le transfert est réversible et peut être mis à jour à tout moment</li>
                      <li>Chaque capsule de transmutation est unique et liée à votre matricule MMD</li>
                      <li>La synchronisation peut être configurée selon vos préférences</li>
                    </ul>
                  </div>
                </div>
              </div>

              <button
                onClick={handleOrderCapsule}
                disabled={isOrdering || !selectedInstanceId || iaInstances.length === 0}
                className="w-full bg-copper-600 text-white px-8 py-4 rounded-md hover:bg-copper-700 transition-all duration-200 shadow-lg border-2 border-copper-700 font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {isOrdering ? (
                  <div className="animate-pulse-soft">Commande en cours...</div>
                ) : (
                  <>
                    <CapsuleIcon className="h-5 w-5" />
                    <span>Commander la MMD Instance Capsule™</span>
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="text-center text-xs text-stone-600 bg-white border border-stone-200 rounded-lg p-4">
            <div className="flex items-center justify-center space-x-2 mb-2">
              <img
                src="https://memomundi.ai/pix/StampMMD.png"
                alt="MemoMundi.ai"
                className="h-4 w-4"
              />
              <span className="font-semibold">TECHNOLOGIE MEMOMUNDI.AI</span>
            </div>
            <p>
              MMD Instance Capsule™ - Technologie brevetée
              <br />
              Compatible avec tous les supports certifiés MMD™
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default InstanceTransfer;
